import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Search,
  Loader2,
  BookOpen,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { leccionesAprendidasAPI } from '../../utils/api';
import { toast } from 'sonner';
import { SemaforoDot } from './Dashboard';

const PAGE_SIZE = 15;

const TopLectores = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.replace(/\/lectores\/?$/, '');

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalLecciones, setTotalLecciones] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // Debounce search
  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(h);
  }, [search]);

  // Reset page when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const load = useCallback(() => {
    setLoading(true);
    leccionesAprendidasAPI
      .rankingLectores({ search: debouncedSearch, page, page_size: PAGE_SIZE })
      .then((r) => {
        setRows(r.data?.items || []);
        setTotal(r.data?.total || 0);
        setTotalLecciones(r.data?.total_lecciones || 0);
      })
      .catch(() => toast.error('Error al cargar el ranking de lectores'))
      .finally(() => setLoading(false));
  }, [debouncedSearch, page]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const startIdx = (page - 1) * PAGE_SIZE;

  return (
    <div className="space-y-6" data-testid="top-lectores-page">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(basePath + '/dashboard')}
          data-testid="back-btn"
          className="mt-1"
        >
          <ArrowLeft size={16} />
        </Button>
        <div className="flex-1">
          <h1
            className="text-2xl sm:text-4xl font-bold text-[#2D2D2D]"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Ranking de Lectores
          </h1>
          <p className="text-sm sm:text-base text-[#5A5A5A] mt-1">
            Ranking de usuarios por cantidad y porcentaje de Lecciones
            Aprendidas leídas.
          </p>
        </div>
      </div>

      {/* Leyenda de semáforo */}
      <div
        className="bg-[#fbf7f0] border border-[#e8dfd0] rounded-lg px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 text-xs text-[#5A5A5A]"
        data-testid="semaforo-legend"
      >
        <div className="font-semibold text-[#2D2D2D] flex items-center gap-2">
          <BookOpen size={14} className="text-[#9d8272]" />
          Leyenda:
        </div>
        <div className="flex items-center gap-2">
          <SemaforoDot color="verde" />
          <span>
            <strong>Verde:</strong> 100% (leyó todas)
          </span>
        </div>
        <div className="flex items-center gap-2">
          <SemaforoDot color="ambar" />
          <span>
            <strong>Ámbar:</strong> ≥ 80% y &lt; 100%
          </span>
        </div>
        <div className="flex items-center gap-2">
          <SemaforoDot color="rojo" />
          <span>
            <strong>Rojo:</strong> &lt; 80%
          </span>
        </div>
      </div>

      {/* Buscador */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888888]"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre..."
            className="pl-9"
            data-testid="search-input"
          />
        </div>
        <div className="text-xs text-[#888888]">
          {total} usuario{total === 1 ? '' : 's'} · {totalLecciones} lección
          {totalLecciones === 1 ? '' : 'es'} disponibles
        </div>
      </div>

      {/* Tabla responsive */}
      <div className="bg-white border border-[#E8E8E7] rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 size={28} className="animate-spin text-[#9d8272]" />
          </div>
        ) : rows.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-sm text-[#888888]">
            {debouncedSearch
              ? 'No se encontraron usuarios con esa búsqueda.'
              : 'No hay usuarios en el universo de lectores.'}
          </div>
        ) : (
          <>
            {/* Tabla desktop */}
            <div className="hidden sm:block overflow-x-auto">
              <table
                className="w-full text-sm"
                data-testid="lectores-table"
              >
                <thead className="bg-[#fbf7f0] border-b border-[#e8dfd0]">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-semibold text-[#2D2D2D] w-12">
                      #
                    </th>
                    <th className="text-left px-4 py-2.5 font-semibold text-[#2D2D2D]">
                      Usuario
                    </th>
                    <th className="text-right px-4 py-2.5 font-semibold text-[#2D2D2D] w-24">
                      Cantidad
                    </th>
                    <th className="text-right px-4 py-2.5 font-semibold text-[#2D2D2D] w-24">
                      %
                    </th>
                    <th className="text-center px-4 py-2.5 font-semibold text-[#2D2D2D] w-24">
                      Semáforo
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E8E8E7]">
                  {rows.map((u, i) => (
                    <tr
                      key={u.user_id}
                      className="hover:bg-[#fafaf8]"
                      data-testid={`lector-row-${u.user_id}`}
                    >
                      <td className="px-4 py-2.5 text-[#888888] tabular-nums">
                        {startIdx + i + 1}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="text-[#2D2D2D]">{u.nombre}</div>
                        <div className="text-[10px] text-[#888888]">
                          {u.email}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-[#2D2D2D]">
                        {u.leidas} / {u.total_lecciones}
                      </td>
                      <td
                        className={`px-4 py-2.5 text-right tabular-nums font-semibold ${
                          u.semaforo === 'verde'
                            ? 'text-[#4a7a28]'
                            : u.semaforo === 'ambar'
                            ? 'text-[#c0855a]'
                            : 'text-[#b85555]'
                        }`}
                      >
                        {u.porcentaje}%
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <div className="inline-flex justify-center">
                          <SemaforoDot color={u.semaforo} size={14} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Cards mobile */}
            <div className="sm:hidden divide-y divide-[#E8E8E7]">
              {rows.map((u, i) => (
                <div
                  key={u.user_id}
                  className="p-3 flex items-center gap-3"
                  data-testid={`lector-card-${u.user_id}`}
                >
                  <div className="w-7 h-7 rounded-full bg-[#f5efe6] text-[#9d8272] flex items-center justify-center text-xs font-bold shrink-0">
                    {startIdx + i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#2D2D2D] truncate">
                      {u.nombre}
                    </div>
                    <div className="text-[10px] text-[#888888] truncate">
                      {u.leidas} / {u.total_lecciones} lecciones
                    </div>
                  </div>
                  <div
                    className={`text-sm font-semibold tabular-nums w-14 text-right ${
                      u.semaforo === 'verde'
                        ? 'text-[#4a7a28]'
                        : u.semaforo === 'ambar'
                        ? 'text-[#c0855a]'
                        : 'text-[#b85555]'
                    }`}
                  >
                    {u.porcentaje}%
                  </div>
                  <SemaforoDot color={u.semaforo} size={14} />
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Paginación */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <div className="text-xs text-[#888888]">
            Página {page} de {totalPages} · {total} resultado
            {total === 1 ? '' : 's'}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="page-prev"
            >
              <ChevronLeft size={14} />
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              data-testid="page-next"
            >
              Siguiente
              <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopLectores;
